import React from 'react';
import { forEach, groupBy } from 'lodash';

// constants
import { activityTypes } from 'constants/activities';
import { affiliateIds } from 'constants/affiliates';
import { exportActionTypes, exportScopeTypes, exportPrimaryStatusTypes } from 'constants/exportSelector';
import { scriptTypes } from 'constants/scripts';

// selectors
import { getFertilityActivityCategoriesOrdered, getApplicationActivityCategoriesOrdered } from 'activities/selectors';
import { hasAffiliateById } from 'affiliates/selectors';
import { 
  getDevicesByName, 
  getPreferredDeviceForActivityType, 
  getDriveDevices, 
  getDevicesForScriptType 
} from 'devices/selectors';
import { getFieldByUuid } from 'fields/selectors';
import { getAllOperations } from 'operations/selectors';
import { isPrimaryScript } from 'scripts/selectors';
import { isApplicationScript } from 'scripts/application-selectors';
import { isFertilityScript } from 'scripts/fertility-selectors';
import { isPlantingScript } from 'scripts/planting-selectors';

// util
import { getMessage } from 'util/locale';
import { getScopeAndScriptTypeFromParams } from 'util/exportSelector';

const generateDeviceOptions = ({ getState, params }) => {
  const options = [];
  const state = getState();
  const devices = getDevicesForScriptType(state);

  devices.sort().forEach((device) => {
    const { label } = device;
    options.push({
      name: label,
      id: `${exportActionTypes.DOWNLOAD_PREFERRED_DEVICE_FORMAT}|${label}|${params}`
    });
  });

  return options;
};

const generateDeviceDrive = ({ getState, actionId }) => {
  const options = [];
  const state = getState();
  const devices = getDriveDevices(state);

  Object.keys(devices).sort().forEach((device) => {
    const { name } = devices[device];
    options.push({
      name,
      id: actionId
    });
  });

  return options;
};

const generateNewOption = ({ getState, params }) => {
  const state = getState();
  const devices = getDevicesByName(state);
  return Object.values(devices).map(({ label }) => {
    const actionId = `${exportActionTypes.SEND_TO_DRIVE}|${label}|${params}`;
    return {
      name: label,
      subOptions: generateDeviceDrive({ getState, params, actionId }),
      title: getMessage(`title-text__export-options--drive`)
    };
  });
};

const generateExportActionOptions = ({ getState, params, activityType }) => {
  const options = [];
  const state = getState();
  const webDrive2Beta = state.launchDarkly?.webDrive2Beta;
  const hasJDConnection = hasAffiliateById(state, affiliateIds.MYJD);
  const isBulkWorkPlansEnabled = state.launchDarkly?.bulkWorkPlans;
  const { scopeType, scriptType } = getScopeAndScriptTypeFromParams(params);

  // John Deere Work Plans option
  if (isBulkWorkPlansEnabled && hasJDConnection && activityType === activityTypes.PLANTING) {
    options.push({
      name: (
        <div data-test-id={`start-${exportActionTypes.CREATE_WORK_PLAN}-${scopeType}-${scriptType}`}>
          {getMessage('button-text__export--jd-work_plans')}
          <small>{getMessage('john-deere-ops-center')}</small>
        </div>
      ),
      id: `${exportActionTypes.CREATE_WORK_PLAN}|${affiliateIds.MYJD}|${params}`
    });
  }

  // myjd option
  options.push({
    name: (
      <div>
        {getMessage(`button-text__export--${affiliateIds.MYJD}`, { connected: hasJDConnection })}
        <small>{getMessage('john-deere-ops-center')}</small>
      </div>
    ),
    id: `${exportActionTypes.EXPORT_TO_AFFILIATE}|${affiliateIds.MYJD}|${params}`
  });

  // preferred device option
  const device = getPreferredDeviceForActivityType(state, activityType);
  
  if (device?.label) {
    const { label } = device;
    
    options.push({
      name: getMessage(`button-text__download-to-preferred-device`, { hasPreferredDevice: true, label }),
      id: `${exportActionTypes.DOWNLOAD_PREFERRED_DEVICE_FORMAT}|${label}|${params}`
    });
  }

  // choose device option
  options.push({
    name: getMessage(`button-text__download-to-preferred-device`, { hasPreferredDevice: false }),
    subOptions: generateDeviceOptions({ getState, params })
  });

  // Send to Drive only able with FF
  if (webDrive2Beta) {
    options.push({
      name: getMessage(`button-text__send-to-drive`),
      subOptions: generateNewOption({ getState, params }),
      title: getMessage(`title-text__export-options--format`)
    });
  }

  return options;
};

const generateActivityOption = ({ getState, labelKey, numScripts, params, activityType }) => {
  return {
    name: getMessage(labelKey, { num: numScripts }),
    subOptions: generateExportActionOptions({
      getState,
      activityType,
      params: params + `|${numScripts}`
    })
  };
};

const generateOperationOrFieldOption = ({ getState, label, scopeType, scripts, scriptType, operationUuid }) => {
  let activities;
  const newActivity = (scriptType) => {
    switch (scriptType) {
      case scriptTypes.PLANTING:
        activities = [activityTypes.PLANTING];
        break;
      case scriptTypes.FERTILITY:
        activities = getFertilityActivityCategoriesOrdered(getState())
        .map(category => category.activityType)
        .concat([scriptTypes.FERTILITY]);
        break;
      case scriptTypes.APPLICATION:
        activities = getApplicationActivityCategoriesOrdered(getState())
        .map(category => category.activityType)
        .concat([scriptTypes.APPLICATION]);
        break;
      default:
        activities = [];
        break;
    }
  };

  newActivity(scriptType);

  // group scripts by activity type
  const scriptsByActivityType = groupBy(scripts, script => script['activity-type']);
  // add all scripts for blanket fertility option
  if (scriptType === scriptTypes.FERTILITY) {
    scriptsByActivityType[scriptTypes.FERTILITY] = scripts;
  }
  // add all scripts for blanket Crop Protection option
  if (scriptType === scriptTypes.APPLICATION) {
    scriptsByActivityType[scriptTypes.APPLICATION] = scripts;
  }

  // loop through activities and create a primary script option and nonprimary script option for each
  const nonprimaryOptions = [];
  const primaryOptions = [];
  activities.forEach((activityType) => {
    const scripts = scriptsByActivityType[activityType] || [];
    const operationUuidForParams = scopeType === exportScopeTypes.OPERATION ? `~${operationUuid}` : '';

    if (scripts.length) {
      nonprimaryOptions.push(
        generateActivityOption({
          getState,
          activityType,
          labelKey: `label-text__export-option--${activityType}`,
          numScripts: scripts.length,
          params: `${scopeType}${operationUuidForParams}|${activityType}|${exportPrimaryStatusTypes.ALL}`
        })
      );
      const primaryScripts = scripts.filter(isPrimaryScript);
      if (primaryScripts.length) {
        primaryOptions.push(
          generateActivityOption({
            getState,
            activityType,
            labelKey: `label-text__export-option--${activityType}--primary`,
            numScripts: primaryScripts.length,
            params: `${scopeType}${operationUuidForParams}|${activityType}|${exportPrimaryStatusTypes.PRIMARY}`
          })
        );
      }
    }
  });

  const subOptions = [].concat(primaryOptions, nonprimaryOptions);

  if (subOptions.length) {
    return {
      name: label,
      subOptions
    };
  }

  return null;
};

const groupScriptsByOperationUuid = ({ getState, scriptsByFieldUuid }) => {
  let operations = {};
  const state = getState();

  Object.keys(scriptsByFieldUuid).forEach((fieldUuid) => {
    const { operationId: operationUuid } = getFieldByUuid(state, fieldUuid);
    operations[operationUuid] = (operations[operationUuid] || []).concat(scriptsByFieldUuid[fieldUuid]);
  });

  return operations;
};

export const generateOperationsOptions = ({ getState, scriptsByFieldUuid, scriptType }) => {
  const state = getState();
  const operations = { all: [] };

  const scriptsByOperationUuid = groupScriptsByOperationUuid({ getState, scriptsByFieldUuid });

  // loop through operations, creating an option for each, plus pushing into array of all operations
  Object.values(getAllOperations(state)).forEach(({ operationName, operationId: operationUuid }) => {
    const scripts = scriptsByOperationUuid[operationUuid];
    const operationOption = generateOperationOrFieldOption({
      getState,
      label: operationName,
      scopeType: exportScopeTypes.OPERATION,
      scripts,
      scriptType,
      operationUuid
    });

    if (operationOption) {
      operations[operationUuid] = [operationOption];
      operations.all.push(operationOption);
    }
  });

  return operations;
};

export const generateFieldOptions = ({ getState, scriptsByFieldUuid, scriptType }) => {
  const state = getState();
  const fields = {};

  // loop through fields, creating an option for each in field hash (or empty array if field has no scripts)
  Object.keys(scriptsByFieldUuid).forEach((fieldUuid) => {
    const scripts = scriptsByFieldUuid[fieldUuid];
    fields[fieldUuid] = scripts
      ? generateOperationOrFieldOption({
        getState,
        label: getFieldByUuid(state, fieldUuid)?.name,
        scopeType: exportScopeTypes.FIELD,
        scripts,
        scriptType
      })
      : [];
  });

  return fields;
};

const generateProductYearTree = ({ getState, scriptsForScriptType, scriptType }) => {
  const years = {};

  const scriptsByProductYear = groupBy(scriptsForScriptType, 'cropping-year');
  // loop through product years, creating operations & fields in hash for each
  forEach(scriptsByProductYear, (scripts, year) => {
    const scriptsByFieldUuid = groupBy(scripts, 'field-uuid');

    years[year] = {
      operations: generateOperationsOptions({ getState, scriptsByFieldUuid, scriptType }),
      fields: generateFieldOptions({ getState, scriptsByFieldUuid, scriptType })
    };
  });

  return years;
};

export const generateScriptTypeTree = ({ getState, rawList }) => {
  let types = {};

  let scriptsForScriptType;
  // loop through each script type, creating product years in has for each
  Object.values(scriptTypes).forEach((scriptType) => {
    switch (scriptType) {
      case scriptTypes.FERTILITY:
        scriptsForScriptType = rawList.filter(isFertilityScript);
        break;
      case scriptTypes.APPLICATION:
        scriptsForScriptType = rawList.filter(isApplicationScript);
        break;
      case scriptTypes.PLANTING:
        scriptsForScriptType = rawList.filter(isPlantingScript);
        break;
      default:
    }

    types[scriptType] = generateProductYearTree({ getState, scriptsForScriptType, scriptType });
  });

  return types;
};

export const parseScriptsForExportOptions = ({ getState, rawList }) => {
  return generateScriptTypeTree({ getState, rawList });
};
